import { ArrowLeft, RefreshCw } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import { ErrorCard, Stat, Td, Th, Verdict, formatDate, formatNumber, formatPercent, signColor } from '../components/research';
import { useProgramDetail } from '../hooks/usePrograms';

export default function ProgramDetail() {
    const { key } = useParams<{ key: string }>();
    const { program, isLoading, error, refresh } = useProgramDetail(key);

    if (error) {
        return (
            <div className="flex flex-col gap-4">
                <Link to="/programs" className="inline-flex items-center gap-2 text-sm" style={{ color: 'var(--foreground-muted)' }}>
                    <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Programs
                </Link>
                <ErrorCard message={error} onRetry={refresh} />
            </div>
        );
    }

    if (!program) {
        return (
            <div className="py-16 text-center" style={{ color: 'var(--foreground-muted)' }}>
                {isLoading ? 'Loading…' : 'Program not found.'}
            </div>
        );
    }

    const combined = program.combined;

    return (
        <div className="flex min-w-0 flex-col gap-4">
            <section className="flex flex-wrap items-start justify-between gap-3 px-1">
                <div className="min-w-0">
                    <Link to="/programs" className="inline-flex items-center gap-2 text-sm" style={{ color: 'var(--foreground-muted)' }}>
                        <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Programs
                    </Link>
                    <h2 className="mt-2 text-3xl font-semibold tracking-tight sm:text-4xl">{program.key}</h2>
                    <p className="mt-1 max-w-3xl text-sm leading-6" style={{ color: 'var(--foreground-muted)' }}>
                        {program.description || 'No description in the program module docstring.'}
                    </p>
                </div>
                <button
                    type="button"
                    onClick={refresh}
                    disabled={isLoading}
                    className="button-secondary inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium"
                >
                    <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} aria-hidden="true" />
                    Refresh
                </button>
            </section>

            {combined ? (
                <div className="surface-card rounded-[28px] p-6 sm:p-7">
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                        <h3 className="text-lg font-semibold">Combined record</h3>
                        <p className="text-xs" style={{ color: 'var(--foreground-muted)' }}>
                            newest run of each bar-carrying sample, pooled — {combined.samples.join(' + ')}
                        </p>
                    </div>
                    <div className="mt-5 grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-6">
                        <Stat label="Net" value={formatPercent(combined.cumulative_return)} color={signColor(combined.cumulative_return)} />
                        <Stat label="Sharpe" value={formatNumber(combined.sharpe)} color={signColor(combined.sharpe)} hint="annualised" />
                        <Stat label="Windows" value={combined.num_windows} />
                        <Stat label="Positive" value={`${combined.windows_positive}/${combined.num_windows}`} />
                        <Stat label="Worst" value={formatPercent(combined.worst_window_return)} color={signColor(combined.worst_window_return)} />
                        <Stat label="Trades" value={combined.num_trades} />
                    </div>
                </div>
            ) : null}

            <div className="surface-card rounded-[28px] p-6 sm:p-7">
                <h3 className="text-lg font-semibold">Mandates</h3>
                <p className="mt-1 text-xs" style={{ color: 'var(--foreground-muted)' }}>
                    Each strategy and the instruments it is allowed to act on. One account, one balance.
                </p>
                <div className="mt-4 flex flex-col gap-3">
                    {(program.mandates ?? []).map((mandate) => (
                        <div key={mandate.strategy} className="rounded-2xl px-4 py-3" style={{ background: 'var(--background-subtle)' }}>
                            <div className="flex flex-wrap items-baseline justify-between gap-2">
                                <span className="font-medium">{mandate.strategy}</span>
                                <span className="text-xs" style={{ color: 'var(--foreground-muted)' }}>
                                    {mandate.instruments.length} instruments
                                </span>
                            </div>
                            <p className="mt-2 break-words text-xs leading-5" style={{ color: 'var(--foreground-muted)' }}>
                                {mandate.instruments.join(' · ')}
                            </p>
                        </div>
                    ))}
                </div>
            </div>

            <div className="surface-card rounded-[28px] p-6 sm:p-7">
                <h3 className="text-lg font-semibold">Runs</h3>
                <div className="mt-4 overflow-x-auto">
                    <table className="w-full min-w-[900px] border-collapse text-sm">
                        <thead>
                            <tr style={{ color: 'var(--foreground-muted)' }}>
                                <Th>Sample</Th>
                                <Th>Period</Th>
                                <Th align="right">Windows</Th>
                                <Th align="right">Instruments</Th>
                                <Th align="right">Net</Th>
                                <Th align="right">Gross</Th>
                                <Th align="right">Costs</Th>
                                <Th align="right">Sharpe</Th>
                                <Th align="right">Trades</Th>
                                <Th>Verdict</Th>
                                <Th align="right">Run at</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {program.runs.map((run) => (
                                <tr key={run.id} className="task-row">
                                    <Td>
                                        <Link to={`/runs/${run.id}`} className="font-medium hover:underline">
                                            {run.sample}
                                        </Link>
                                        {run.window_days !== 7 ? (
                                            <span className="ml-1 text-xs opacity-70">{run.window_days}d</span>
                                        ) : null}
                                    </Td>
                                    <Td color="var(--foreground-muted)">
                                        {formatDate(run.period_start)} → {formatDate(run.period_end)}
                                    </Td>
                                    <Td align="right" mono color="var(--foreground-muted)">
                                        {run.windows_positive ?? '—'}/{run.num_windows}
                                    </Td>
                                    <Td align="right" mono color="var(--foreground-muted)">
                                        {run.constituent_count}
                                    </Td>
                                    <Td align="right" mono color={signColor(run.cumulative_return)}>
                                        {formatPercent(run.cumulative_return)}
                                    </Td>
                                    <Td align="right" mono color="var(--foreground-muted)">
                                        {formatPercent(run.gross_return)}
                                    </Td>
                                    <Td align="right" mono color="var(--foreground-muted)">
                                        {run.cost_drag === null ? '—' : `-${Math.abs(run.cost_drag).toFixed(2)}%`}
                                    </Td>
                                    <Td align="right" mono color={signColor(run.sharpe)}>
                                        {formatNumber(run.sharpe)}
                                    </Td>
                                    <Td align="right" mono color="var(--foreground-muted)">
                                        {run.num_trades ?? '—'}
                                    </Td>
                                    <Td>
                                        <Verdict passed={run.passed} />
                                    </Td>
                                    <Td align="right" color="var(--foreground-muted)">
                                        {formatDate(run.insert_time)}
                                    </Td>
                                </tr>
                            ))}
                            {program.runs.length === 0 ? (
                                <tr>
                                    <td colSpan={11} className="py-8 text-center" style={{ color: 'var(--foreground-muted)' }}>
                                        This program has not been backtested yet.
                                    </td>
                                </tr>
                            ) : null}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
